import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { JOB_CATEGORIES, EMPLOYMENT_TYPES, SALARY_PERIODS } from "@/lib/jobs";
import { Briefcase, X, Plus, Loader2, ShieldCheck } from "lucide-react";

export default function JobCreate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");

  const [form, setForm] = useState({
    title: "",
    company_name: "",
    category: "Tech",
    description: "",
    location: "",
    remote_ok: false,
    work_type: "in_person",
    employment_type: "full_time",
    salary_min: "",
    salary_max: "",
    currency: "USD",
    salary_period: "year",
    apply_questions: [],
    expires_in_days: 30,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!editId) return;
    api.get(`/jobs/${editId}`).then(r => {
      const j = r.data || {};
      setForm({
        title: j.title || "",
        company_name: j.company_name || "",
        category: j.category || "Tech",
        description: j.description || "",
        location: j.location || "",
        remote_ok: !!j.remote_ok,
        work_type: j.work_type || (j.remote_ok ? "remote" : "in_person"),
        employment_type: j.employment_type || "full_time",
        salary_min: j.salary_min ?? "",
        salary_max: j.salary_max ?? "",
        currency: j.currency || "USD",
        salary_period: j.salary_period || "year",
        apply_questions: j.apply_questions || [],
        expires_in_days: 30,
      });
    }).catch(e => setErr(formatErr(e)));
  }, [editId]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addQ = () => update("apply_questions", [...form.apply_questions, ""]);
  const setQ = (i, v) => update("apply_questions", form.apply_questions.map((q, idx) => idx === i ? v : q));
  const rmQ = (i) => update("apply_questions", form.apply_questions.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min === "" ? null : Number(form.salary_min),
        salary_max: form.salary_max === "" ? null : Number(form.salary_max),
        apply_questions: form.apply_questions.filter(q => q.trim()),
      };
      if (editId) {
        await api.patch(`/jobs/${editId}`, payload);
        nav(`/jobs/${editId}`);
      } else {
        const { data } = await api.post("/jobs", payload);
        nav(`/jobs/${data.id}`);
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!user) {
    return <div className="max-w-md mx-auto p-10 text-center">
      <p className="text-[#4A4A4A]">Please sign in to post a job.</p>
    </div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 py-10" data-testid="job-create-page">
      <p className="overline mb-1">{editId ? "Edit job" : "New job"}</p>
      <h1 className="h-display text-4xl">Hire your next great human.</h1>
      <p className="text-[#4A4A4A] mt-2">Real names. Real stories. Real fit.</p>

      <form onSubmit={submit} className="card p-6 mt-7 space-y-5" data-testid="job-form">
        <Field label="Job title" required>
          <input className="input" required minLength={3} maxLength={120}
            value={form.title} onChange={(e)=>update("title", e.target.value)}
            placeholder="e.g. Senior Surf Instructor" data-testid="job-title-input"/>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Company / your name" required>
            <input className="input" required value={form.company_name}
              onChange={(e)=>update("company_name", e.target.value)} data-testid="job-company"/>
          </Field>
          <Field label="Category" required>
            <select className="input" value={form.category}
              onChange={(e)=>update("category", e.target.value)} data-testid="job-category-select">
              {JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Job description" required>
          <textarea required rows={6} className="input" minLength={20} maxLength={8000}
            value={form.description} onChange={(e)=>update("description", e.target.value)}
            placeholder="What they'll do, the perfect candidate, perks, how you work."
            data-testid="job-description-input"/>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Location">
            <input className="input" value={form.location}
              onChange={(e)=>update("location", e.target.value)}
              placeholder="City, Country (optional)" data-testid="job-location-input"/>
          </Field>
          <Field label="Employment">
            <select className="input" value={form.employment_type}
              onChange={(e)=>update("employment_type", e.target.value)} data-testid="job-employment-select">
              {EMPLOYMENT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Where is the work done?">
          <div className="grid grid-cols-3 gap-2" data-testid="job-work-type">
            {[
              { id: "in_person", label: "In person", hint: "Onsite at your location" },
              { id: "remote",    label: "Remote",    hint: "Can work from anywhere" },
              { id: "hybrid",    label: "Hybrid",    hint: "Mix of onsite + remote" },
            ].map(opt => (
              <label key={opt.id}
                className={`card p-3 cursor-pointer transition-colors ${form.work_type === opt.id ? "border-[#0057FF] bg-[#F5F5F5]" : "hover:border-[#8C8C8C]"}`}
                data-testid={`job-work-type-${opt.id}`}>
                <input type="radio" name="work_type" value={opt.id}
                  checked={form.work_type === opt.id}
                  onChange={()=>{
                    update("work_type", opt.id);
                    update("remote_ok", opt.id === "remote" || opt.id === "hybrid");
                  }}
                  className="sr-only"/>
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="text-[11px] text-[#8C8C8C] mt-1">{opt.hint}</p>
              </label>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Salary min">
            <input type="number" min="0" step="100" className="input" value={form.salary_min}
              onChange={(e)=>update("salary_min", e.target.value)} placeholder="0"
              data-testid="job-salary-min"/>
          </Field>
          <Field label="Salary max">
            <input type="number" min="0" step="100" className="input" value={form.salary_max}
              onChange={(e)=>update("salary_max", e.target.value)} placeholder="0"
              data-testid="job-salary-max"/>
          </Field>
          <Field label="Per">
            <select className="input" value={form.salary_period}
              onChange={(e)=>update("salary_period", e.target.value)} data-testid="job-salary-period">
              {SALARY_PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Currency">
          <input className="input" maxLength={6} value={form.currency}
            onChange={(e)=>update("currency", e.target.value.toUpperCase())} data-testid="job-currency"/>
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Application questions (optional)</span>
            <button type="button" onClick={addQ} className="btn-ghost text-xs" data-testid="job-add-question">
              <Plus size={12}/> Add question
            </button>
          </div>
          <div className="space-y-2">
            {form.apply_questions.map((q, i) => (
              <div key={i} className="flex gap-2" data-testid={`job-question-${i}`}>
                <input className="input flex-1" maxLength={200} placeholder={`Question ${i+1}`}
                  value={q} onChange={(e)=>setQ(i, e.target.value)}/>
                <button type="button" onClick={()=>rmQ(i)} className="btn-ghost"><X size={14}/></button>
              </div>
            ))}
          </div>
        </div>

        {!editId && (
          <Field label="Expires in (days)">
            <input type="number" min="1" max="180" className="input" value={form.expires_in_days}
              onChange={(e)=>update("expires_in_days", e.target.value)} data-testid="job-expires"/>
          </Field>
        )}

        {err && <p className="text-sm text-[#B84B4B]" data-testid="job-form-error">{err}</p>}

        <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1">
          <ShieldCheck size={12}/> Free to post · You can boost to top of feed after publishing.
        </p>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={()=>nav("/jobs")} className="btn-ghost flex-1">Cancel</button>
          <button className="btn-primary flex-1" disabled={busy} data-testid="job-submit">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Publishing…</> : <><Briefcase size={14}/> {editId ? "Save changes" : "Publish job"}</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">{label}{required && <span className="text-[#0057FF]"> *</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
